import React, { useState, useEffect } from "react";
import { Table, Input, Row, Col, Skeleton, Button } from "antd";
import { Link } from "react-router-dom";

import { SearchOutlined } from "@ant-design/icons";

import {
  Messages,
  Notify,
  openNotification,
  paginationEnum,
  util
} from '@seims/shared';

import service from "../../services/partnershipService";
import { recordError } from "../../utils/logService";



const PartnerList = ({ hasUpdated }) => {
  const [partners, setPartners] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [pageSize, setPageSize] = useState(paginationEnum.DEFAULT_PAGE_SIZE);
  const [total, setTotal] = useState(paginationEnum.DEFAULT_MAX_TOTAL);
  const [currentPage, setCurrentPage] = useState(
    paginationEnum.DEFAULT_FIRST_PAGE
  );
  const [firstRecord, setFirstRecord] = useState(
    paginationEnum.DEFAULT_FIRST_RECORD
  );
  const [pagingDisabled, setPagingDisabled] = useState(false);
  const [isNotSearchable, setIsNotSearchable] = useState(true);
  const [isSearchedResults, setIsSearchedResults] = useState(false);

  let msg = "";

  useEffect(() => {
    getPartners({ first: 0, pageSize: 10});
    
    if (hasUpdated === true) {
      getPartners({ first: 0, pageSize: 10});
    }
  }, [hasUpdated]);

  const handleInputChange = (e) => {
    const { value: searchValue } = e.target;
    setSearchText(searchValue);
    setIsNotSearchable(searchValue === "");

    if (searchValue === "") {
      setFirstRecord(0);
      setCurrentPage(1);
      getPartners({ first: 0, pageSize: 10, searchText: searchValue });

    } else {
      // globalSearch(searchValue);
      getPartners({ first: 0, pageSize: 10, searchText: searchValue });
    }
  };

  const handleSearchReset = (e) => {
    const { value: searchValue } = e.target;
    setSearchText(searchValue);
    setIsNotSearchable(searchValue === "");

    if (searchValue === "") {
      getPartners({ first: 0, pageSize: 10, searchText: searchValue });
    }
  };

  const handleSearchBtn = () => {
    setFirstRecord(0);
    getPartners({ first: 0, pageSize: 10, searchText: searchText });
  }

  const getPartners = async ({
    first = 0,
    max = 10,
    searchText = "",
  }) => {
    const entity = "partners";

    try {
      setLoading(true);

      if (max > total) setTotal(max);

      const response = await service.getPartnerships({
        first: first,
        max: max,
        searchText: searchText,
      });

      if (response) {
        var partnerData = [];
        const { results: data, total } = response?.data;

        if (response.status === 204) {
          // msg = util.parseMessage(Messages.NO_DATA, "Partners");
          // openNotification("Partners", msg, Notify.ERROR);
          setPartners([]);
          setFilteredData([]);
          setLoading(false);
          setPagingDisabled(true);
          return;
        }

        partnerData = data.map((element) => ({
          key: element.uuid + "x" + element.id.toString(),
          id: element.id,
          name: element.name,
          type: element.partnerType.value,
          partnerType: element.partnerType,
          initiationDate: element.initiationDate,
          telephoneNumber: element.telephoneNumber,
          email: element.email,
          contributions: element.contributions,
          otherContribution: element.otherContribution,
          active: element.active,
          archived: element.archived,
          initiationDate: element.initiationDate,
          lastEngagementDate: element.lastEngagementDate,
          locality: element.locality,
          reportedOn: element.reportedOn,
          userId: element.userId,
        }));

        setPartners(partnerData);
        setFilteredData(partnerData);
        setLoading(false);
        setTotal(total);
        setPagingDisabled(false);
      }
    } catch (error) {
      msg = util.parseMessage(Messages.GET_FAILURE, entity);
      openNotification("Partners", msg, Notify.ERROR);
      recordError(error);
      // setLoading(false);

    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "1",
      defaultSortOrder: "ascend",
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "2",
      sorter: (a, b) => a.type.localeCompare(b.type),
    },
    { title: "Contact Number", dataIndex: "telephoneNumber", key: "3" },
    { title: "Email Address", dataIndex: "email", key: "4" },
    {
      title: "Actions",
      dataIndex: "edit",
      key: "5",
      render: (_, record) => {
        return (
          <>
            <Link
              to={`/partnership/${record.key}`}
              state={{ from: "/partnership", data: record }}
            >
              Edit
            </Link>
          </>
        );
      },
    },
  ];

  const onPageChange = (page, total) => {
    const keepCurrentPage = pageSize === total;
    const first = keepCurrentPage ? (page - 1) * pageSize : 0;
    const newPage = keepCurrentPage ? page : 1;
  
    setFirstRecord(first);
    setPageSize(total);
    setCurrentPage(newPage);
    getPartners({ first: first, max: pageSize, searchText: searchText });
  };

  const onRowChange = (row) => {
    setPageSize(row.pageSize);
  };

  return (
    <>
      <div>List of Partners</div>
      <Row>
        <Col xs={16} sm={16} md={8}>
          <Input
            placeholder="Search Partner"
            type="text"
            className="search-bar"
            onChange={handleSearchReset}
            // onBlur={handleInputChange}
            onPaste={handleInputChange}
            onPressEnter={handleInputChange}
            value={searchText}
            allowClear
            size="large"
            prefix={<SearchOutlined />}
          />
        </Col>
        <Col xs={8} sm={8} md={8}>
          <Button
            disabled={isNotSearchable}
            key="Search"
            value="Search"
            type="primary"
            onClick={handleSearchBtn}
            style={{ marginLeft: 10 }}
          >
            Search
          </Button>
        </Col>
      </Row>
      <div className="space"></div>
      {loading ? (
        <Skeleton active avatar />
      ) : (
        <Table
          dataSource={filteredData}
          rowKey={(record) => record.email}
          columns={columns}
          loading={loading}
          onChange={onRowChange}
          bordered
          pagination={{
            disabled: pagingDisabled,
            defaultCurrent: 1,
            current: currentPage,
            defaultPageSize: pageSize,
            total: total,
            pageSize: pageSize,
            showSizeChanger: true,
            showQuickJumper: true,
            onChange: onPageChange,
            pageSizeOptions: [10, 20, 50, 100],
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} ${
                total > 1 ? "Partners" : "Partner"
              }`,
          }}
          size="middle"
        />
      )}
    </>
  );
};

export default PartnerList;
